import type { KayaState } from "../domain/types";
import type { PendingDrink } from "../domain/water";
import type { LinkedPart, LinkedWrite } from "./linked";

const BASE = (import.meta.env.VITE_API_URL || "/api/v1").replace(/\/$/, "");
const TOKEN_KEY = "kaya-token";

export type UserStatus = "pending" | "approved" | "suspended";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: "user" | "admin";
  /** Anything but "approved" means the app shows the waiting screen instead of itself. A build
   *  that predates approval sent no status, so an absent one is read as approved. */
  status: UserStatus;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(t: string | null): void {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
  }
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(BASE + path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: "Bearer " + token } : {}),
      ...(init.headers || {}),
    },
  });

  if (!res.ok) {
    let msg = res.statusText;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch {
      /* non-JSON error body */
    }
    // A proxy with no server behind it answers with a bare status line; "Internal Server Error"
    // on a phone screen says nothing the person can act on.
    if (res.status >= 500 && msg === res.statusText) msg = "The server did not answer. Try again in a moment.";
    throw new ApiError(res.status, msg);
  }
  return (await res.json()) as T;
}

export const api = {
  register: (email: string, password: string, name: string) =>
    req<{ token: string; user: AuthUser }>("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, name }),
    }),

  login: (email: string, password: string) =>
    req<{ token: string; user: AuthUser }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  me: () => req<{ user: AuthUser }>("/auth/me"),

  pull: () => req<{ state: KayaState | null; updatedAt: number; rev: number }>("/state"),

  push: (state: KayaState, updatedAt: number) =>
    req<{ state: KayaState; updatedAt: number; rev: number }>("/state", {
      method: "PUT",
      body: JSON.stringify({ state, updatedAt }),
    }),

  health: () => req<{ ok: boolean }>("/health"),

  /* ---------- steps, written by automations rather than by the app ---------- */

  pullSteps: (from: string, to: string) =>
    req<{ days: StepDay[] }>(`/steps?from=${from}&to=${to}`),

  /** Raw counter readings for a range, in epoch milliseconds. Differenced client-side. */
  pullSamples: (fromMs: number, toMs: number) =>
    req<{ samples: { t: number; steps: number }[] }>(`/steps/samples?from=${fromMs}&to=${toMs}`),

  pushSteps: (days: { date: string; steps: number }[], source: string) =>
    req<{ ok: boolean; written: number }>("/steps", {
      method: "POST",
      body: JSON.stringify({ days, source }),
    }),

  listKeys: () => req<{ keys: ApiKeyInfo[] }>("/auth/keys"),

  /** The plaintext key is returned once and never again. */
  createKey: (name: string) =>
    req<{ id: string; name: string; prefix: string; key: string }>("/auth/keys", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),

  deleteKey: (id: string) => req<{ ok: boolean }>(`/auth/keys/${id}`, { method: "DELETE" }),

  /* ---------- push reminders and drinks from notifications (ADR-045) ---------- */

  pushKey: () => req<{ key: string | null }>("/push/key"),

  pushSubscribe: (subscription: PushSubscriptionJSON, platform: PushPlatform) =>
    req<{ ok: boolean }>("/push/subscribe", {
      method: "POST",
      body: JSON.stringify({ subscription, platform }),
    }),

  pushUnsubscribe: (endpoint: string) =>
    req<{ ok: boolean }>("/push/subscribe", { method: "DELETE", body: JSON.stringify({ endpoint }) }),

  pushStatus: (endpoint: string) =>
    req<PushStatus>(`/push/status?endpoint=${encodeURIComponent(endpoint)}`),

  pushSeen: (endpoint: string) =>
    req<{ ok: boolean; known: boolean }>("/push/seen", { method: "POST", body: JSON.stringify({ endpoint }) }),

  drinkInbox: () => req<{ drinks: PendingDrink[] }>("/water/inbox"),

  drinkAck: (ids: string[]) =>
    req<{ ok: boolean }>("/water/ack", { method: "POST", body: JSON.stringify({ ids }) }),

  /* ---------- linked accounts (ADR-046) ---------- */

  links: () => req<{ links: LinkInfo[] }>("/links"),

  linkAsk: (email: string, label: string) =>
    req<{ link: LinkInfo }>("/links", { method: "POST", body: JSON.stringify({ email, label }) }),

  linkAccept: (id: string) => req<{ link: LinkInfo }>(`/links/${id}/accept`, { method: "POST" }),

  linkLabel: (id: string, label: string) =>
    req<{ link: LinkInfo }>(`/links/${id}`, { method: "PATCH", body: JSON.stringify({ label }) }),

  linkRemove: (id: string) => req<{ ok: boolean }>(`/links/${id}`, { method: "DELETE" }),

  linkState: (id: string) => req<LinkedState>(`/links/${id}/state`),

  linkNotify: (id: string, missedWeek: boolean) =>
    req<{ link: LinkInfo }>(`/links/${id}/notify`, { method: "PUT", body: JSON.stringify({ missedWeek }) }),

  linkWrite: (id: string, write: LinkedWrite) =>
    req<{ state: KayaState; stateMeta: { updatedAt: number; rev: number }; overruled: LinkedPart[] }>(
      `/links/${id}/state`,
      { method: "PUT", body: JSON.stringify(write) }
    ),

  /* ---------- admin ---------- */

  adminUsers: () => req<{ users: AdminUser[] }>("/admin/users"),

  adminUser: (id: string) => req<AdminUserDetail>(`/admin/users/${id}`),

  adminPatchUser: (id: string, patch: Partial<Pick<AdminUser, "name" | "email" | "status" | "role">>) =>
    req<{ user: AuthUser }>(`/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  adminSetPassword: (id: string, password: string) =>
    req<{ ok: boolean }>(`/admin/users/${id}/password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),

  adminPutState: (id: string, state: KayaState) =>
    req<{ updatedAt: number; rev: number }>(`/admin/users/${id}/state`, {
      method: "PUT",
      body: JSON.stringify({ state }),
    }),

  adminDeleteUser: (id: string) => req<{ ok: boolean }>(`/admin/users/${id}`, { method: "DELETE" }),
};

export interface AdminUser extends AuthUser {
  createdAt: string;
  approvedAt: number | null;
  lastSeenAt: number | null;
  lastSyncAt: number | null;
  rev: number;
  keys: number;
}

export interface AdminUserDetail {
  user: AdminUser;
  state: KayaState | null;
  stateMeta: { updatedAt: number; rev: number };
  summary: { sessions: number; lastLog: string | null; cycleOn: boolean };
  steps: { date: string; steps: number; source: string }[];
  keys: { id: string; name: string; prefix: string; lastUsedAt: number | null }[];
}

/** One link as either end sees it: `reader` — I read them; `subject` — they read me. */
export interface LinkInfo {
  id: string;
  role: "reader" | "subject";
  status: "pending" | "active";
  /** what the reader calls them ("Mum") */
  label: string;
  byAdmin: boolean;
  askedByMe: boolean;
  /** the account at the other end; null only if it was deleted mid-request */
  person: { id: string; name: string; email: string } | null;
  createdAt: string;
  acceptedAt: number | null;
  /** the reader is told when a Steady week goes by with no strength day (ADR-056); absent from an
   *  older server */
  notifyMissedWeek?: boolean;
}

export interface LinkedState {
  person: { id: string; name: string; email: string; label: string };
  state: KayaState | null;
  stateMeta: { updatedAt: number; rev: number };
  /** their devices subscribed to Kaya's pushes */
  devices: number;
  /** the missed-week notice is on, and how many of the reader's own devices it could reach (ADR-056) */
  notifyMissedWeek?: boolean;
  readerDevices?: number;
  steps: StepDay[];
}

export type PushPlatform ="android" | "ios" | "desktop" | "other";

export interface PushStatus {
  /** the server has VAPID keys and runs the scheduler */
  configured: boolean;
  /** the server holds this device's subscription */
  subscribed: boolean;
  lastOkAt: number | null;
  lastError: string | null;
}

export interface StepDay {
  date: string;
  steps: number;
  source: string;
  updatedAt: number;
}

export interface ApiKeyInfo {
  id: string;
  name: string;
  prefix: string;
  scope: string;
  lastUsedAt: number | null;
  createdAt: string;
}
